import * as React from 'react';
import {useEffect, useRef, useState} from "react";
import {BaseRangeSlider} from "../_baseRangeSlider";

import 'components/forms/formComponents/FormRangeSlider/formRangeSlider.scss';

import {Input} from "@atoms/FormElements/Input";
import { FormattedMessage } from 'react-intl';

const formatNumber = (num: number): string =>
    num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');

const parseNumber = (str: string): number =>
    parseInt(str.replace(/\s/g, ''), 10);

interface Props {
    onSliderChange: (value: [number, number]) => void;
    valueSuffix?: string;
    maxValueSuffix?: string;
    defaultValue?: [number, number];
    min: number;
    max: number;
    step?: number;
    minRange?: number;
    label?: ((value: number) => React.ReactNode) | null;
    ref?: HTMLInputElement;
}

export const FormRangeSlider : React.FunctionComponent<Props> = ({onSliderChange, valueSuffix, maxValueSuffix, ref: _ref, defaultValue, ...rest}) => {
    const [value, setValue] = useState<[number, number]>(defaultValue ? defaultValue : [0, 10]);
    const [inputValues, setInputValues] = useState<[string, string]>([
        formatNumber(value[0]),
        formatNumber(value[1]),
    ]);

    const sliderWrapperRef = useRef<HTMLDivElement>(null);
    const [debouncedValue, setDebouncedValue] = useState<[number, number]>(value);

    useEffect(() => {
        if (defaultValue && (defaultValue[0] !== value[0] || defaultValue[1] !== value[1])) {
            setValue(defaultValue);
            setInputValues([formatNumber(defaultValue[0]), formatNumber(defaultValue[1])]);
        }
    }, [defaultValue?.[0], defaultValue?.[1]]);

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedValue(value);
        }, 300);

        return () => clearTimeout(handler);
    }, [value]);

    useEffect(() => {
        onSliderChange(debouncedValue);
    }, [debouncedValue]);

    useEffect(() => {
        if (sliderWrapperRef.current) {
            const focusableEls = sliderWrapperRef.current.querySelectorAll<HTMLElement>(
                'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
            );
            focusableEls.forEach(el => el.setAttribute('tabindex', '-1'));
        }
    });

    const onInputChange = (index: 0 | 1, newValue: string) => {
        const digitsOnly = newValue.replace(/\D/g, '');
        const updatedInputs = [...inputValues] as [string, string];

        if (digitsOnly === '') {
            updatedInputs[index] = '';
        } else {
            const num = parseInt(digitsOnly, 10);
            updatedInputs[index] = !isNaN(num) ? formatNumber(num) : '';
        }

        setInputValues(updatedInputs);
    }

    const onInputBlur = (index: 0 | 1, newValue: string) => {
        const fallback = index === 0 ? rest.min : rest.max;
        const parsedValue = newValue.trim() === '' ? fallback : parseNumber(newValue);
        const resolved = isNaN(parsedValue) ? fallback : parsedValue;

        const updatedValue = [...value] as [number, number];
        updatedValue[index] = resolved;

        const {minRange} = {...rest};
        const roundedValue = minRange ? Math.ceil(updatedValue[index] / minRange) * minRange : updatedValue[index];
        updatedValue[index] = roundedValue;

        const updatedInputs = [...inputValues] as [string, string];
        updatedInputs[index] = formatNumber(roundedValue);
        setInputValues(updatedInputs);

        if (updatedValue[0] <= updatedValue[1]) {
            setValue(updatedValue);
            onSliderChange(updatedValue);
        }
    }

    const handleSliderChange = (newValue: [number, number]) => {
        setValue(newValue);
        setInputValues([formatNumber(newValue[0]), formatNumber(newValue[1])]);
    };

    return (
        <div className="form-slider">
            <div className="form-slider__inputs">
                <div className="form-slider__input-container">
                    <span className='visually-hidden'><FormattedMessage id="jm.a11y.from" /></span>
                    <Input
                        type="text"
                        id="minValue"
                        name="minValue"
                        messages={[]}
                        value={inputValues[0]}
                        onChange={(value) => onInputChange(0, value)}
                        onBlur={(value) => onInputBlur(0, value)}
                    />

                    {valueSuffix && <span className="form-slider__value-suffix">{valueSuffix}</span>}
                </div>

                <div className="form-slider__divider">
                    <span className='visually-hidden'><FormattedMessage id="jm.a11y.to" /></span>
                </div>

                <div className="form-slider__input-container">
                    <Input
                        type="text"
                        id="minValue"
                        name="minValue"
                        messages={[]}
                        value={inputValues[1]}
                        onChange={(value) => onInputChange(1, value)}
                        onBlur={(value) => onInputBlur(1, value)}
                    />
                    {valueSuffix && <span className="form-slider__value-suffix">{maxValueSuffix && value[1] >= rest.max ? maxValueSuffix : valueSuffix}</span>}
                </div>
            </div>

            <div className="form-slider__component" aria-hidden tabIndex={-1} ref={sliderWrapperRef}>
                <BaseRangeSlider
                    value={value}
                    onChange={handleSliderChange}
                    {...rest}
                />
            </div>

        </div>
    );
}
